import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Settings, Building2, Mail, Phone, MapPin, Globe, 
  Instagram, Twitter, Youtube, Music, Save, Loader2,
  Upload, Camera, Palette, Link2, User, Sparkles, Linkedin, Lock, Eye, EyeOff,
  Landmark, CreditCard, AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import ProducerLayout from '../../components/producer/ProducerLayout';

const ProducerSettingsPage = () => {
  const [producer, setProducer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });
  const [formData, setFormData] = useState({
    business_name: '',
    contact_name: '',
    email: '',
    phone: '',
    description: '',
    website: '',
    province: '',
    city: '',
    social_links: {
      instagram: '',
      twitter: '',
      youtube: '',
      spotify: '',
      linkedin: '',
      email: ''
    },
    branding: {
      logo_url: '',
      // Custom aura colors
      aura_enabled: false,
      aura_color_1: 'violet-500',
      aura_color_2: '#FFD700',
      aura_color_3: 'pink-500'
    },
    bank_info: {
      bank_name: '',
      account_holder: '',
      cbu: '',
      alias: '',
      cuit: '',
      is_locked: false
    }
  });
  const [bankLocked, setBankLocked] = useState(false);

  useEffect(() => {
    loadProducer();
  }, []);

  const loadProducer = async () => {
    try {
      const response = await api.get('/producers/me');
      const data = response.data;
      setProducer(data);
      setFormData({
        business_name: data.business_name || '',
        contact_name: data.contact_name || '',
        email: data.email || '',
        phone: data.phone || '',
        description: data.description || '',
        website: data.website || '',
        province: data.province || '',
        city: data.city || '',
        social_links: {
          instagram: data.social_links?.find(s => s.platform === 'instagram')?.url || '',
          twitter: data.social_links?.find(s => s.platform === 'twitter')?.url || '',
          youtube: data.social_links?.find(s => s.platform === 'youtube')?.url || '',
          spotify: data.social_links?.find(s => s.platform === 'spotify')?.url || '',
          linkedin: data.social_links?.find(s => s.platform === 'linkedin')?.url || '',
          email: data.social_links?.find(s => s.platform === 'email')?.url || ''
        },
        branding: {
          logo_url: data.branding?.logo_url || '',
          aura_enabled: data.branding?.aura_enabled || false,
          aura_color_1: data.branding?.aura_color_1 || 'violet-500',
          aura_color_2: data.branding?.aura_color_2 || '#FFD700',
          aura_color_3: data.branding?.aura_color_3 || 'pink-500'
        },
        bank_info: {
          bank_name: data.bank_info?.bank_name || '',
          account_holder: data.bank_info?.account_holder || '',
          cbu: data.bank_info?.cbu || '',
          alias: data.bank_info?.alias || '',
          cuit: data.bank_info?.cuit || '',
          is_locked: data.bank_info?.is_locked || false
        }
      });
      setBankLocked(data.bank_info?.is_locked || false);
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Solo se permiten imágenes');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('La imagen no puede superar 5MB');
      return;
    }

    setUploading(true);
    try {
      const sigResponse = await api.get('/cloudinary/signature?folder=producer_logos');
      const { signature, timestamp, cloud_name, api_key, folder } = sigResponse.data;

      const formDataUpload = new FormData();
      formDataUpload.append('file', file);
      formDataUpload.append('api_key', api_key);
      formDataUpload.append('timestamp', timestamp);
      formDataUpload.append('signature', signature);
      formDataUpload.append('folder', folder);

      const uploadResponse = await fetch(
        `https://api.cloudinary.com/v1_1/${cloud_name}/image/upload`,
        { method: 'POST', body: formDataUpload }
      );
      
      const uploadData = await uploadResponse.json();
      
      if (uploadData.secure_url) {
        setFormData(prev => ({
          ...prev,
          branding: { ...prev.branding, logo_url: uploadData.secure_url }
        }));
        toast.success('Logo subido correctamente');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error al subir logo');
    } finally {
      setUploading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        business_name: formData.business_name,
        contact_name: formData.contact_name,
        phone: formData.phone,
        description: formData.description,
        website: formData.website,
        province: formData.province,
        city: formData.city,
        social_links: [
          { platform: 'instagram', url: formData.social_links.instagram },
          { platform: 'twitter', url: formData.social_links.twitter },
          { platform: 'youtube', url: formData.social_links.youtube },
          { platform: 'spotify', url: formData.social_links.spotify },
          { platform: 'linkedin', url: formData.social_links.linkedin },
          { platform: 'email', url: formData.social_links.email }
        ].filter(link => link.url),
        branding: {
          logo_url: formData.branding.logo_url,
          aura_enabled: formData.branding.aura_enabled,
          aura_color_1: formData.branding.aura_color_1,
          aura_color_2: formData.branding.aura_color_2,
          aura_color_3: formData.branding.aura_color_3
        },
        bank_info: {
          bank_name: formData.bank_info.bank_name,
          account_holder: formData.bank_info.account_holder,
          cbu: formData.bank_info.cbu,
          alias: formData.bank_info.alias,
          cuit: formData.bank_info.cuit
        }
      };

      const response = await api.put('/producers/me', payload);
      
      // Check if bank info was just locked
      if (response.data?.bank_locked) {
        toast.success('Datos bancarios guardados y bloqueados por seguridad');
        setBankLocked(true);
      } else {
        toast.success('Configuración guardada');
      }
      
      // Update localStorage
      const cachedData = localStorage.getItem('producer_data');
      if (cachedData) {
        const updated = { ...JSON.parse(cachedData), ...payload };
        localStorage.setItem('producer_data', JSON.stringify(updated));
      }
      
      loadProducer();
    } catch (error) {
      console.error('Error:', error);
      const errorMsg = error.response?.data?.detail || 'Error al guardar';
      toast.error(errorMsg);
    } finally {
      setSaving(false);
    }
  };

  const handleChangePassword = async () => {
    // Validations
    if (!passwordData.current_password) {
      toast.error('Ingresá tu contraseña actual');
      return;
    }
    if (!passwordData.new_password) {
      toast.error('Ingresá la nueva contraseña');
      return;
    }
    if (passwordData.new_password.length < 6) {
      toast.error('La nueva contraseña debe tener al menos 6 caracteres');
      return;
    }
    if (passwordData.new_password !== passwordData.confirm_password) {
      toast.error('Las contraseñas no coinciden');
      return;
    }

    setChangingPassword(true);
    try {
      await api.post('/producers/me/change-password', {
        current_password: passwordData.current_password,
        new_password: passwordData.new_password
      });
      toast.success('Contraseña actualizada correctamente');
      setPasswordData({ current_password: '', new_password: '', confirm_password: '' });
    } catch (error) {
      console.error('Error:', error);
      toast.error(error.response?.data?.detail || 'Error al cambiar contraseña');
    } finally {
      setChangingPassword(false);
    }
  };

  // Generate aura gradient preview
  const getAuraGradient = () => {
    if (!formData.branding.aura_enabled) return 'transparent';
    return `linear-gradient(135deg, ${formData.branding.aura_color_1}, ${formData.branding.aura_color_2}, ${formData.branding.aura_color_3})`;
  };

  if (loading) {
    return (
      <ProducerLayout currentPage="settings">
        <div className="flex items-center justify-center h-64">
          <Loader2 className="animate-spin text-violet-500" size={32} />
        </div>
      </ProducerLayout>
    );
  }

  return (
    <ProducerLayout currentPage="settings">
      <div className="p-4 lg:p-8 max-w-4xl mx-auto" data-testid="producer-settings-page">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl lg:text-3xl font-black text-white flex items-center gap-3">
              <Settings className="text-violet-500" />
              Configuración
            </h1>
            <p className="text-zinc-400 mt-1">Personaliza tu perfil y marca</p>
          </div>
          <Button
            onClick={handleSave}
            disabled={saving}
            className="bg-gradient-to-r from-violet-600 to-pink-600 text-white"
          >
            {saving ? <Loader2 className="animate-spin mr-2" size={16} /> : <Save size={16} className="mr-2" />}
            Guardar
          </Button>
        </div>

        <div className="space-y-6">
          {/* Logo Section */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-zinc-900 border border-zinc-800 rounded-xl p-6"
          >
            <h2 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
              <Camera size={20} className="text-violet-500" />
              Logo de tu Productora
            </h2>
            
            <div className="flex flex-col md:flex-row items-start gap-6">
              {/* Logo Preview */}
              <div className="relative group">
                <div className="w-32 h-32 bg-zinc-800 rounded-2xl overflow-hidden flex items-center justify-center">
                  {formData.branding.logo_url ? (
                    <img 
                      src={formData.branding.logo_url} 
                      alt="Logo"
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <Building2 className="text-zinc-600" size={48} />
                  )}
                </div>
                <label className="absolute inset-0 bg-black/50 rounded-2xl flex items-center justify-center opacity-0 group-hover:opacity-100 cursor-pointer transition-opacity">
                  {uploading ? (
                    <Loader2 className="animate-spin text-white" size={24} />
                  ) : (
                    <Upload className="text-white" size={24} />
                  )}
                  <input
                    type="file"
                    className="hidden"
                    accept="image/*"
                    onChange={handleLogoUpload}
                    disabled={uploading}
                  />
                </label>
              </div>
              
              <div className="text-sm text-zinc-400 space-y-2">
                <p>PNG o JPG. Max 5MB.</p>
                <p>Recomendado: 400x400px</p>
                <label className="inline-flex items-center gap-2 px-4 py-2 bg-zinc-800 rounded-lg cursor-pointer hover:bg-zinc-700 transition-colors">
                  <Upload size={16} />
                  <span>Subir logo</span>
                  <input
                    type="file"
                    className="hidden"
                    accept="image/*"
                    onChange={handleLogoUpload}
                    disabled={uploading}
                  />
                </label>
              </div>
            </div>
          </motion.section>

          {/* Custom Aura Section */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.05 }}
            className="bg-zinc-900 border border-zinc-800 rounded-xl p-6"
          >
            <h2 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
              <Sparkles size={20} className="text-violet-500" />
              Aura Personalizada
            </h2>
            <p className="text-sm text-zinc-400 mb-4">
              Crea un efecto visual único para tu productora eligiendo tus propios colores.
            </p>
            
            {/* Enable Aura Toggle */}
            <div className="flex items-center gap-3 mb-6">
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.branding.aura_enabled}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    branding: { ...prev.branding, aura_enabled: e.target.checked }
                  }))}
                  className="w-5 h-5 rounded border-zinc-700 bg-zinc-800 text-violet-500 focus:ring-violet-500"
                />
                <span className="text-white font-medium">Activar aura personalizada</span>
              </label>
            </div>
            
            {formData.branding.aura_enabled && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                className="space-y-6"
              >
                {/* Aura Preview */}
                <div className="flex justify-center">
                  <div className="relative">
                    <div 
                      className="w-40 h-40 rounded-full blur-2xl opacity-60 absolute inset-0 -m-4"
                      style={{ background: getAuraGradient() }}
                    />
                    <div className="w-32 h-32 bg-zinc-800 rounded-full flex items-center justify-center relative z-10">
                      {formData.branding.logo_url ? (
                        <img 
                          src={formData.branding.logo_url} 
                          alt="Logo"
                          className="w-full h-full object-cover rounded-full"
                        />
                      ) : (
                        <Building2 className="text-zinc-600" size={48} />
                      )}
                    </div>
                    <p className="text-center text-sm text-zinc-500 mt-4">Vista previa del aura</p>
                  </div>
                </div>
                
                {/* Color Pickers */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <Label className="text-zinc-400">Color 1</Label>
                    <div className="flex items-center gap-2 mt-2">
                      <input
                        type="color"
                        value={formData.branding.aura_color_1}
                        onChange={(e) => setFormData(prev => ({
                          ...prev,
                          branding: { ...prev.branding, aura_color_1: e.target.value }
                        }))}
                        className="w-12 h-12 rounded-lg cursor-pointer border-2 border-zinc-700"
                      />
                      <Input
                        value={formData.branding.aura_color_1}
                        onChange={(e) => setFormData(prev => ({
                          ...prev,
                          branding: { ...prev.branding, aura_color_1: e.target.value }
                        }))}
                        className="bg-zinc-800 border-zinc-700 font-mono uppercase"
                        placeholder="violet-500"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <Label className="text-zinc-400">Color 2</Label>
                    <div className="flex items-center gap-2 mt-2">
                      <input
                        type="color"
                        value={formData.branding.aura_color_2}
                        onChange={(e) => setFormData(prev => ({
                          ...prev,
                          branding: { ...prev.branding, aura_color_2: e.target.value }
                        }))}
                        className="w-12 h-12 rounded-lg cursor-pointer border-2 border-zinc-700"
                      />
                      <Input
                        value={formData.branding.aura_color_2}
                        onChange={(e) => setFormData(prev => ({
                          ...prev,
                          branding: { ...prev.branding, aura_color_2: e.target.value }
                        }))}
                        className="bg-zinc-800 border-zinc-700 font-mono uppercase"
                        placeholder="#FFD700"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <Label className="text-zinc-400">Color 3</Label>
                    <div className="flex items-center gap-2 mt-2">
                      <input
                        type="color"
                        value={formData.branding.aura_color_3}
                        onChange={(e) => setFormData(prev => ({
                          ...prev,
                          branding: { ...prev.branding, aura_color_3: e.target.value }
                        }))}
                        className="w-12 h-12 rounded-lg cursor-pointer border-2 border-zinc-700"
                      />
                      <Input
                        value={formData.branding.aura_color_3}
                        onChange={(e) => setFormData(prev => ({
                          ...prev,
                          branding: { ...prev.branding, aura_color_3: e.target.value }
                        }))}
                        className="bg-zinc-800 border-zinc-700 font-mono uppercase"
                        placeholder="pink-500"
                      />
                    </div>
                  </div>
                </div>
                
                {/* Gradient Preview Bar */}
                <div>
                  <Label className="text-zinc-400 mb-2 block">Gradiente resultante</Label>
                  <div 
                    className="h-8 rounded-lg"
                    style={{ background: getAuraGradient() }}
                  />
                </div>
              </motion.div>
            )}
          </motion.section>

          {/* Business Info */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-zinc-900 border border-zinc-800 rounded-xl p-6"
          >
            <h2 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
              <Building2 size={20} className="text-violet-500" />
              Información de la Productora
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-zinc-400">Nombre de la Productora</Label>
                <Input
                  value={formData.business_name}
                  onChange={(e) => setFormData(prev => ({ ...prev, business_name: e.target.value }))}
                  placeholder="Tu productora"
                  className="bg-zinc-800 border-zinc-700 mt-1"
                />
              </div>
              <div>
                <Label className="text-zinc-400">Nombre de Contacto</Label>
                <Input
                  value={formData.contact_name}
                  onChange={(e) => setFormData(prev => ({ ...prev, contact_name: e.target.value }))}
                  placeholder="Nombre del responsable"
                  className="bg-zinc-800 border-zinc-700 mt-1"
                />
              </div>
              <div>
                <Label className="text-zinc-400">Email</Label>
                <Input
                  value={formData.email}
                  disabled
                  className="bg-zinc-800/50 border-zinc-700 mt-1 text-zinc-500"
                />
              </div>
              <div>
                <Label className="text-zinc-400">Teléfono</Label>
                <Input
                  value={formData.phone}
                  onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                  placeholder="1123456789"
                  className="bg-zinc-800 border-zinc-700 mt-1"
                />
              </div>
              <div>
                <Label className="text-zinc-400">Provincia</Label>
                <Input
                  value={formData.province}
                  onChange={(e) => setFormData(prev => ({ ...prev, province: e.target.value }))}
                  placeholder="Buenos Aires"
                  className="bg-zinc-800 border-zinc-700 mt-1"
                />
              </div>
              <div>
                <Label className="text-zinc-400">Ciudad</Label>
                <Input
                  value={formData.city}
                  onChange={(e) => setFormData(prev => ({ ...prev, city: e.target.value }))}
                  placeholder="CABA"
                  className="bg-zinc-800 border-zinc-700 mt-1"
                />
              </div>
              <div className="md:col-span-2">
                <Label className="text-zinc-400">Sitio Web</Label>
                <Input
                  value={formData.website}
                  onChange={(e) => setFormData(prev => ({ ...prev, website: e.target.value }))}
                  placeholder="https://tuproductora.com"
                  className="bg-zinc-800 border-zinc-700 mt-1"
                />
              </div>
              <div className="md:col-span-2">
                <Label className="text-zinc-400">Descripción</Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Cuéntanos sobre tu productora..."
                  className="bg-zinc-800 border-zinc-700 mt-1"
                  rows={3}
                />
              </div>
            </div>
          </motion.section>

          {/* Social Links */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 }}
            className="bg-zinc-900 border border-zinc-800 rounded-xl p-6"
          >
            <h2 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
              <Link2 size={20} className="text-violet-500" />
              Redes Sociales
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-zinc-400 flex items-center gap-2">
                  <Instagram size={16} className="text-pink-500" />
                  Instagram
                </Label>
                <Input
                  value={formData.social_links.instagram}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    social_links: { ...prev.social_links, instagram: e.target.value }
                  }))}
                  placeholder="https://instagram.com/tuproductora"
                  className="bg-zinc-800 border-zinc-700 mt-1"
                />
              </div>
              <div>
                <Label className="text-zinc-400 flex items-center gap-2">
                  <Twitter size={16} className="text-blue-400" />
                  Twitter / X
                </Label>
                <Input
                  value={formData.social_links.twitter}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    social_links: { ...prev.social_links, twitter: e.target.value }
                  }))}
                  placeholder="https://twitter.com/tuproductora"
                  className="bg-zinc-800 border-zinc-700 mt-1"
                />
              </div>
              <div>
                <Label className="text-zinc-400 flex items-center gap-2">
                  <Youtube size={16} className="text-red-500" />
                  YouTube
                </Label>
                <Input
                  value={formData.social_links.youtube}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    social_links: { ...prev.social_links, youtube: e.target.value }
                  }))}
                  placeholder="https://youtube.com/@tuproductora"
                  className="bg-zinc-800 border-zinc-700 mt-1"
                />
              </div>
              <div>
                <Label className="text-zinc-400 flex items-center gap-2">
                  <Music size={16} className="text-green-500" />
                  Spotify
                </Label>
                <Input
                  value={formData.social_links.spotify}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    social_links: { ...prev.social_links, spotify: e.target.value }
                  }))}
                  placeholder="https://open.spotify.com/user/..."
                  className="bg-zinc-800 border-zinc-700 mt-1"
                />
              </div>
              <div>
                <Label className="text-zinc-400 flex items-center gap-2">
                  <Linkedin size={16} className="text-blue-600" />
                  LinkedIn
                </Label>
                <Input
                  value={formData.social_links.linkedin}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    social_links: { ...prev.social_links, linkedin: e.target.value }
                  }))}
                  placeholder="https://linkedin.com/company/tuproductora"
                  className="bg-zinc-800 border-zinc-700 mt-1"
                />
              </div>
              <div>
                <Label className="text-zinc-400 flex items-center gap-2">
                  <Mail size={16} className="text-yellow-500" />
                  Email de contacto
                </Label>
                <Input
                  value={formData.social_links.email}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    social_links: { ...prev.social_links, email: e.target.value }
                  }))}
                  placeholder="contacto@tuproductora.com"
                  className="bg-zinc-800 border-zinc-700 mt-1"
                />
              </div>
            </div>
          </motion.section>

          {/* Bank Info Section */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.35 }}
            className="bg-zinc-900 border border-zinc-800 rounded-xl p-6"
          >
            <h2 className="text-lg font-bold text-white mb-2 flex items-center gap-2">
              <Landmark size={20} className="text-violet-500" />
              Datos Bancarios para Liquidaciones
              {bankLocked && (
                <span className="ml-2 px-2 py-0.5 bg-red-500/20 text-red-400 text-xs rounded-full flex items-center gap-1">
                  <Lock size={12} /> Bloqueado
                </span>
              )}
            </h2>
            
            {bankLocked ? (
              <div className="space-y-4">
                <div className="p-4 bg-zinc-800/50 border border-zinc-700 rounded-lg">
                  <p className="text-zinc-400 text-sm mb-4">
                    Tus datos bancarios están guardados y bloqueados por seguridad.
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                    <div>
                      <span className="text-zinc-500">Banco:</span>
                      <span className="text-white ml-2">{formData.bank_info.bank_name || '-'}</span>
                    </div>
                    <div>
                      <span className="text-zinc-500">Titular:</span>
                      <span className="text-white ml-2">{formData.bank_info.account_holder || '-'}</span>
                    </div>
                    <div>
                      <span className="text-zinc-500">CBU:</span>
                      <span className="text-white ml-2 font-mono">
                        {formData.bank_info.cbu ? `****${formData.bank_info.cbu.slice(-4)}` : '-'}
                      </span>
                    </div>
                    <div>
                      <span className="text-zinc-500">Alias:</span>
                      <span className="text-white ml-2">{formData.bank_info.alias || '-'}</span>
                    </div>
                    <div>
                      <span className="text-zinc-500">CUIT:</span>
                      <span className="text-white ml-2 font-mono">
                        {formData.bank_info.cuit ? `**-*******-${formData.bank_info.cuit.slice(-1)}` : '-'}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="p-3 bg-yellow-500/10 border border-yellow-500/20 rounded-lg">
                  <p className="text-yellow-500 text-xs flex items-center gap-2">
                    <AlertCircle size={14} />
                    Para modificar tus datos bancarios, contactá a <a href="mailto:central@bravoproductora.com" className="underline font-medium">central@bravoproductora.com</a>
                  </p>
                </div>
              </div>
            ) : (
              <>
                <p className="text-zinc-500 text-sm mb-4 flex items-start gap-2">
                  <AlertCircle size={16} className="text-yellow-500 mt-0.5 flex-shrink-0" />
                  Las liquidaciones de tus ventas se realizan dentro de los 5 días hábiles posteriores al evento.
                </p>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-zinc-400">Banco</Label>
                    <Input
                      value={formData.bank_info.bank_name}
                      onChange={(e) => setFormData(prev => ({
                        ...prev,
                        bank_info: { ...prev.bank_info, bank_name: e.target.value }
                      }))}
                      placeholder="Ej: Banco Galicia, Santander, etc."
                      className="bg-zinc-800 border-zinc-700 mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-zinc-400">Titular de la Cuenta</Label>
                    <Input
                      value={formData.bank_info.account_holder}
                      onChange={(e) => setFormData(prev => ({
                        ...prev,
                        bank_info: { ...prev.bank_info, account_holder: e.target.value }
                      }))}
                      placeholder="Nombre completo del titular"
                      className="bg-zinc-800 border-zinc-700 mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-zinc-400">CBU (22 dígitos)</Label>
                    <Input
                      value={formData.bank_info.cbu}
                      onChange={(e) => setFormData(prev => ({
                        ...prev,
                        bank_info: { ...prev.bank_info, cbu: e.target.value.replace(/\D/g, '').slice(0, 22) }
                      }))}
                      placeholder="0000000000000000000000"
                      className="bg-zinc-800 border-zinc-700 mt-1 font-mono"
                      maxLength={22}
                    />
                  </div>
                  <div>
                    <Label className="text-zinc-400">Alias CBU (opcional)</Label>
                    <Input
                      value={formData.bank_info.alias}
                      onChange={(e) => setFormData(prev => ({
                        ...prev,
                        bank_info: { ...prev.bank_info, alias: e.target.value.toUpperCase() }
                      }))}
                      placeholder="MI.ALIAS.CBU"
                      className="bg-zinc-800 border-zinc-700 mt-1"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <Label className="text-zinc-400">CUIT/CUIL del Titular</Label>
                    <Input
                      value={formData.bank_info.cuit}
                      onChange={(e) => setFormData(prev => ({
                        ...prev,
                        bank_info: { ...prev.bank_info, cuit: e.target.value.replace(/\D/g, '').slice(0, 11) }
                      }))}
                      placeholder="20-12345678-9"
                      className="bg-zinc-800 border-zinc-700 mt-1 font-mono max-w-xs"
                      maxLength={11}
                    />
                  </div>
                </div>
                
                <div className="mt-4 p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
                  <p className="text-red-400 text-xs flex items-center gap-2">
                    <Lock size={14} />
                    <strong>Importante:</strong> Una vez guardados, los datos bancarios quedarán bloqueados por seguridad.
                  </p>
                </div>
              </>
            )}
            
            <div className="mt-4 p-3 bg-yellow-500/10 border border-yellow-500/20 rounded-lg">
              <p className="text-yellow-500 text-xs flex items-center gap-2">
                <CreditCard size={14} />
                Tus datos bancarios son confidenciales y solo se usarán para transferir las liquidaciones de tus eventos.
              </p>
            </div>
          </motion.section>

          {/* Change Password Section */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="bg-zinc-900 border border-zinc-800 rounded-xl p-6"
          >
            <h2 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
              <Lock size={20} className="text-violet-500" />
              Cambiar Contraseña
            </h2>
            
            <div className="space-y-4 max-w-md">
              <div>
                <Label className="text-zinc-400">Contraseña Actual</Label>
                <div className="relative mt-1">
                  <Input
                    type={showCurrentPassword ? "text" : "password"}
                    value={passwordData.current_password}
                    onChange={(e) => setPasswordData(prev => ({ ...prev, current_password: e.target.value }))}
                    placeholder="Ingresá tu contraseña actual"
                    className="bg-zinc-800 border-zinc-700 pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-white"
                  >
                    {showCurrentPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>
              
              <div>
                <Label className="text-zinc-400">Nueva Contraseña</Label>
                <div className="relative mt-1">
                  <Input
                    type={showNewPassword ? "text" : "password"}
                    value={passwordData.new_password}
                    onChange={(e) => setPasswordData(prev => ({ ...prev, new_password: e.target.value }))}
                    placeholder="Mínimo 6 caracteres"
                    className="bg-zinc-800 border-zinc-700 pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-white"
                  >
                    {showNewPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>
              
              <div>
                <Label className="text-zinc-400">Confirmar Nueva Contraseña</Label>
                <Input
                  type="password"
                  value={passwordData.confirm_password}
                  onChange={(e) => setPasswordData(prev => ({ ...prev, confirm_password: e.target.value }))}
                  placeholder="Repetí la nueva contraseña"
                  className="bg-zinc-800 border-zinc-700 mt-1"
                />
              </div>
              
              <Button
                onClick={handleChangePassword}
                disabled={changingPassword}
                className="bg-violet-500 hover:bg-[#B4941F] text-black font-bold"
              >
                {changingPassword ? (
                  <Loader2 className="animate-spin mr-2" size={16} />
                ) : (
                  <Lock className="mr-2" size={16} />
                )}
                Cambiar Contraseña
              </Button>
            </div>
          </motion.section>
        </div>
      </div>
    </ProducerLayout>
  );
};

export default ProducerSettingsPage;
